// Independent Contractor / Subcontracted Staffing Agreement (Policy A-2.4) —
// one per staffing agency relationship, not per individual (A-2.4 explicitly
// allows an "entity" as the contracting party). Unlike offer letters/consent
// forms, this is never signed in-app — it's downloaded here, executed
// externally (SignWell/DocuSign, same as other agreements this business
// already uses), and the signed PDF is uploaded back via the Contracting
// Agencies admin view. So this only ever renders the unsigned template with
// blank signature lines — there's no "signed" variant to generate.
import React from 'react';
import { pdf } from '@react-pdf/renderer';
import { Document, Page, View, Text, styles, COLORS, Letterhead, SigBlock, ensureCursiveFont } from './pdfPrimitives';

const TERMINATION_NOTICE_DAYS = 7;

function AgreementPdf({ agencyName }) {
  ensureCursiveFont();
  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <Letterhead />
        <Text style={{ fontFamily: 'Helvetica-Bold', fontSize: 13, color: COLORS.navy, textAlign: 'center', marginBottom: 4 }}>
          INDEPENDENT CONTRACTOR / SUBCONTRACTED STAFFING AGREEMENT
        </Text>
        <Text style={{ fontSize: 8, color: COLORS.mid, textAlign: 'center', marginBottom: 14 }}>Policy A-2.4 — Contracted &amp; Subcontracted Staffing Agreements</Text>

        <Text style={styles.legal}>
          This Agreement is entered into between <Text style={{ fontFamily: 'Helvetica-Bold' }}>Hard Shoulder Group LLC</Text>, d/b/a <Text style={{ fontFamily: 'Helvetica-Bold' }}>A Heart For Care</Text> ("Agency"), and <Text style={{ fontFamily: 'Helvetica-Bold' }}>{agencyName || '_______________________________'}</Text> ("Contractor"), together with the individual(s) Contractor assigns to provide services under this Agreement (each, an "Assigned Individual").
        </Text>

        <Text style={styles.sectionHdr}>1. Services</Text>
        <Text style={styles.legal}>Contractor, through its Assigned Individual(s), will provide home health and/or personal care services to Agency's clients as authorized under each client's Plan of Care, in accordance with all conditions of participation applicable to the service being purchased under this Agreement.</Text>

        <Text style={styles.sectionHdr}>2. Agency Retains Responsibility</Text>
        <Text style={styles.legal}>Hard Shoulder Group LLC retains full responsibility for, and will assure, the Contractor's performance of all administrative, supervisory, professional, and service-delivery responsibilities relevant to meeting Georgia Pediatric Program (GAPP) and Healthcare Facility Regulation Division (HFRD) requirements.</Text>

        <Text style={styles.sectionHdr}>3. Compliance</Text>
        <Text style={styles.legal}>Contractor and each Assigned Individual will comply with all applicable local, state, and federal laws, rules, and regulations, and will adhere to GAPP and HFRD policies as they currently exist or are later amended.</Text>

        <Text style={styles.sectionHdr}>4. Staff Qualifications</Text>
        <Text style={styles.legal}>Contractor confirms that each Assigned Individual delivering services under this Agreement meets the same staff qualification, screening, and training requirements as Agency's direct employees (Policies A-2.1 and A-2.2), and will provide current documentation of background check clearance (GCHEXS determination or license verification, as applicable), TB screening, and CPR/First Aid certification for each Assigned Individual prior to that individual providing any client services.</Text>

        <Text style={styles.sectionHdr}>5. Case Conferences</Text>
        <Text style={styles.legal}>Contractor will participate as needed in Agency's case conferences to coordinate a client's care.</Text>

        <Text style={styles.sectionHdr}>6. Supervision</Text>
        <Text style={styles.legal}>Assigned Individuals are subject to the same supervisory requirements as Agency's direct employees under Policy A-3.7 — clinical oversight by the RN Supervisor, an identified supervisor of record documented in the Assigned Individual's personnel file and the client's record, and supervisory visits at the intervals Policy A-3.7 requires.</Text>

        <Text style={styles.sectionHdr}>7. Term &amp; Review</Text>
        <Text style={styles.legal}>This Agreement is reviewed and renewed at least annually, or upon any material change in the services provided. Either party may terminate this Agreement with at least {TERMINATION_NOTICE_DAYS} days' written notice.</Text>

        <Text style={styles.sectionHdr}>8. Records</Text>
        <Text style={styles.legal}>A copy of this executed Agreement is retained in Agency's compliance files and made available to DCH/HFRD upon request.</Text>

        <View style={{ marginTop: 14 }}>
          <SigBlock signers={[
            { label: 'CONTRACTOR (on behalf of the above-named entity)', name: '', date: '' },
            { label: 'A HEART FOR CARE (on behalf of Hard Shoulder Group LLC)', name: '', date: '' },
          ]} />
        </View>
      </Page>
    </Document>
  );
}

export async function renderContractingAgreementBlob(agencyName) {
  return pdf(<AgreementPdf agencyName={agencyName} />).toBlob();
}

export { blobToBase64 } from './trainingCertificatePdf';
